/**
 * アクティビティ帯グラフの色と数値表記。
 * コンポーネントと分けておくのは Fast Refresh のため（.tsx がコンポーネント以外を
 * export すると HMR が全体再読み込みに落ちる）。
 */

/**
 * 工程（項目）別の内訳に使うパレット。ブリジット・ライリーの
 * 明るいストライプ（青・コーラル・ティール・イエロー系）から抽出した色。
 *
 * 配列の順番がそのまま隣り合う区間の並びになるため、暖色と寒色・濃淡が
 * 交互に来るように並べてある（同系色が隣接して境目が消えるのを防ぐ）。
 * 淡い色ほど後ろに置き、区間が少ないうちは彩度の高い色から使われるようにする。
 */
export const COLUMN_PALETTE = [
  "#41b7fb", // スカイブルー
  "#fc9678", // コーラル
  "#01b9b6", // ティール
  "#fdd068", // ゴールデンイエロー
  "#577aba", // コーンフラワーブルー
  "#48cd80", // フレッシュグリーン
  "#575c60", // チャコール
  "#a8cf70", // ライトグリーン
  "#92e6d7", // ミント
  "#a4e0fd", // ライトブルー
  "#fef6b4", // ペールイエロー
  "#c7e997", // ペールグリーン
];

/**
 * 担当者別の内訳に使うパレット。ブリジット・ライリーのもう 1 枚、
 * ペリウィンクルやラベンダーを含む柔らかいストライプから抽出した色。
 *
 * 工程用と色域を分けているので、同じ帯グラフの中で「工程の色」と
 * 「担当者の色」を取り違えにくい。
 */
export const STAFF_PALETTE = [
  "#797fe2", // ペリウィンクル
  "#e09339", // オレンジ
  "#45adc6", // ティールブルー
  "#d95d8a", // マゼンタピンク
  "#a8c261", // オリーブグリーン
  "#70aaeb", // スカイブルー
  "#e4857a", // コーラル
  "#d4a8e0", // ラベンダー
  "#d7d985", // ペールイエローグリーン
  "#b4aec5", // ウォームグレー
  "#b7c9f4", // ライトペリウィンクル
  "#f0d2d8", // ペールピンク
];

/** パレットから外れたキー（「その他」など）に使う色 */
export const ACTIVITY_NEUTRAL_COLOR = "var(--soremo-light-gray)";

/**
 * キーの並びから色を割り当てる関数を作る。
 *
 * 名前のハッシュで選ぶ方式だと、項目が数個あるだけで同じ色が高確率で衝突し、
 * 隣り合う区間が同色になって帯が読めなくなる。そこで「データ全体のキーを
 * 並べた順番」で色を配ることで、パレットの色数までは必ず別の色になるようにする。
 *
 * 渡すキーは呼び出し側で安定した順序（名前順など）に揃えること。月を切り替えても
 * 同じ工程・同じ担当者が同じ色であってほしいので、絞り込み後ではなく
 * **全期間のキー**から作る。
 */
export const buildActivityColorScale = (
  keys: string[],
  palette: string[],
  // eslint-disable-next-line no-unused-vars
): ((key: string) => string) => {
  const colorByKey = new Map<string, string>();
  for (const key of keys) {
    if (colorByKey.has(key)) continue;
    colorByKey.set(key, palette[colorByKey.size % palette.length]);
  }
  return (key: string) => colorByKey.get(key) ?? ACTIVITY_NEUTRAL_COLOR;
};

/**
 * HEX 色を白と混ぜた**不透明**なパステル色にする。タスクボードの列ラベル（th）と
 * アクティビティ集計の「項目ごと」帯で同じ見た目になるよう共有する。
 * 不透明にするのは、表の sticky ヘッダーが本文に重なるため（C-5）。
 */
export const mixWithWhite = (hex: string, ratio: number): string => {
  const n = parseInt(hex.slice(1), 16);
  if (Number.isNaN(n) || hex.length !== 7) return hex;
  const mix = (c: number) => Math.round(c * ratio + 255 * (1 - ratio));
  return `rgb(${mix((n >> 16) & 255)}, ${mix((n >> 8) & 255)}, ${mix(n & 255)})`;
};

/**
 * 列ラベル背景に使う配色の濃さ（0〜1）。文字の可読性を優先して淡めにする。
 * テーブルの列ラベル行とアクティビティの「項目ごと」帯で共通。
 */
export const COLUMN_LABEL_COLOR_RATIO = 0.35;

/** 時間の表示（小数第 1 位まで、末尾の 0 は落とす） */
export const formatHours = (hours: number): string =>
  `${Math.round(hours * 10) / 10}h`;

/** 全体に占める割合の表示 */
export const formatShare = (hours: number, total: number): string =>
  total > 0 ? `${Math.round((hours / total) * 100)}%` : "0%";
